import {Injectable} from '@angular/core';
import {AuthRepository} from '../repository/auth.repository';
import {LoginRequest, LoginResponse} from '../repository/entity/auth.entity';
import {BehaviorSubject, catchError, firstValueFrom, from, Observable, of, switchMap, tap,} from 'rxjs';
import {ApiResponse, ApiResponseError,} from '../../base/repository/entity/base.entity';
import {Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {NgxSpinnerService} from 'ngx-spinner';
import {ToastrService} from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _userLogin$ = new BehaviorSubject<LoginResponse>({
    accessToken: '',
    email: '',
    fullName: '',
    phoneNumber: '',
    refreshToken: '',
    username: '',
    companies: [],
  });

  constructor(
    private _authRepo: AuthRepository,
    private router: Router,
    private spinner: NgxSpinnerService,
    private toastService: ToastrService
  ) {
  }

  get userLogin(): Observable<LoginResponse> {
    return this._userLogin$;
  }

  set userLogin(value: LoginResponse) {
    this._userLogin$.next(value);
  }

  getUserLogin(): Observable<LoginResponse> {
    const checkUserLogin$ = this.onCheckUserLogin();
    return from(checkUserLogin$).pipe(switchMap(() => this.userLogin));
  }

  async onCheckUserLogin() {
    const userExist = this._userLogin$.value.accessToken !== '';
    if (!userExist) {
      const token = localStorage.getItem('refresh_token');
      if (token) {
        const refreshedUser$ = this._authRepo
          .refresh()
          .pipe(catchError(() => of(null)));

        const data = await firstValueFrom(refreshedUser$);
        if (data) {
          this.setToken(data);
        } else {
          await this.router.navigate(['/auth/login']);
        }
      } else {
        await this.router.navigate(['/auth/login']);
      }
    }
  }

  doLogin(
    payload: LoginRequest
  ): Observable<ApiResponse<LoginResponse> | null> {
    return this._authRepo.login(payload).pipe(
      catchError(this.handleConfigurationError.bind(this)),
      tap((data) => {
        if (data?.result) {
          this.setToken(data);
          this.router.navigate(['/dashboard']);
        }
      })
    );
  }

  isAuthenticated() {
    const accessToken = localStorage.getItem('access_token');
    return !!accessToken; // Example: return true if token exists, otherwise false
  }

  private setToken(data: ApiResponse<LoginResponse>) {
    this.userLogin = data.result;
    localStorage.setItem('access_token', data.result.accessToken);
    localStorage.setItem('refresh_token', data.result.refreshToken);
    if (data.result.companies && data.result.companies.length > 0) {
      localStorage.setItem(
        'company_code',
        data.result.companies[0].companyCode
      );
    }
  }

  private handleConfigurationError(err: HttpErrorResponse): Observable<null> {
    const errors: ApiResponseError[] = err?.error?.errors;
    if (errors && errors.length > 0) {
      const errorMessages: string[] = errors.map((error) => error.message);
      this.toastService.error(errorMessages.join(', '));
    }
    this.spinner.hide();
    return of(null);
  }
}
